import './NotificationListItem.scss';

import { Card } from '@mui/material';
import classNames from 'classnames';
import { NotificationResourceTypeEnum } from 'domain/notification/NotificationResourceTypeEnum';
import { TranslationType } from 'domain/translation/Translation.repository';
import { formatDate } from 'helper/date.helper';
import { TagPlayer } from 'primary/Components/TagPlayer/TagPlayer';
import { routesConfig } from 'primary/Configs/Routes.config';
import { NotificationGeneralEventContent } from 'primary/MessageList/contents/NotificationGeneralEventContent';
import { NotificationRefuseEventEventContent } from 'primary/MessageList/contents/NotificationRefuseTeamEventContent';
import { NotificationScoreUpdatedContent } from 'primary/MessageList/contents/NotificationScoreUpdatedContent';
import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { NotificationTypeEnum } from '../../domain/notification/NotificationTypeEnum';
import { decrementNumberUnviewed } from '../../domain/notification/store/NotificationSlice';
import { UnpyNotification } from '../../domain/notification/UnpyNotification';
import { KeyValue } from '../Components/KeyValue/KeyValue';
import { useContextDependency } from '../hooks/useContextDependency';
import { useTranslate } from '../hooks/useTranslate';
import { NotificationInvitationFriendContent } from './contents/NotificationInvitationFriendContent';
import { NotificationInvitationParticipationEventContent } from './contents/NotificationInvitationParticipationEventContent';
import { NotificationInvitationStructureContent } from './contents/NotificationInvitationStructureContent';
import { NotificationInvitationTeamEventContent } from './contents/NotificationInvitationTeamEventContent';
import { NotificationJoinStructureContent } from './contents/NotificationJoinStructureContent';
import { NotificationParticipationEventContent } from './contents/NotificationParticipationEventContent';

interface notificationListItemProps {
  notification: UnpyNotification;
  reFetchNotification: () => void;
  closePreview?: () => void;
  className?: string;
}

// @ts-ignore
const ACTION_COMP_BY_NOTIFICATION_TYPE: {
  [key in NotificationTypeEnum]: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => ReactNode;
} = {
  [NotificationTypeEnum.INVITATION_STRUCTURE]: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationInvitationStructureContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  JOIN_STRUCTURE: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationJoinStructureContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  JOIN_STRUCTURE_FROM_INVITATION_LINK: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationJoinStructureContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  PARTICIPATION_EVENT: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationParticipationEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  INVITATION_TEAM_PARTICIPATION_EVENT: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationInvitationTeamEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  SCORE_UPDATED: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationScoreUpdatedContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  INVITATION_PARTICIPATION_EVENT: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationInvitationParticipationEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  PARTICIPATION_EVENT_DECLINED: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationRefuseEventEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  INVITATION_FRIEND: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationInvitationFriendContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  INVITATION_FRIEND_RESPONSE: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationInvitationFriendContent
      closePreview={closePreview}
      isResponse
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  INVITATION_STRUCTURE_RESPONSE: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationInvitationStructureContent
      closePreview={closePreview}
      isResponse
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  REVERT_EVENT_PLACE: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationGeneralEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  MAJ_DECLINE_EVENT: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationGeneralEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  NEW_MESSAGE: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationInvitationFriendContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  INVITATION_RESPONSE_DECLINE: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
  ) => <></>,
  INVITATION_RESPONSE_ACCEPT: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
  ) => <></>,
  EVENT_CANCELED: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationGeneralEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  EVENT_START: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationGeneralEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  EVENT_DELAYED: (notification: UnpyNotification, reFetchNotification: () => void) => (
    <></>
  ),
  EVENT_FINISHED: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationGeneralEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  EVENT_ANNONCE: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationGeneralEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  EVENT_CANCEL_DELAYED: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationGeneralEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  PARTICIPATION_EVENT_ACCEPTED: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
    closePreview?: () => void,
  ) => (
    <NotificationGeneralEventContent
      closePreview={closePreview}
      notification={notification}
      reFetchNotification={reFetchNotification}
    />
  ),
  PARTICIPATION_PAYMENT_REQUIRED: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
  ) => <></>,
  PARTICIPATION_PAYMENT_RECEIVED: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
  ) => <></>,
  [NotificationTypeEnum.ABONNEMENT_UPDATE]: (
    notification: UnpyNotification,
    reFetchNotification: () => void,
  ) => <></>,
};

export function getNavigationLinkFromRessourceKey(
  keyValue: NotificationResourceTypeEnum,
  value: string,
  resources: Record<NotificationResourceTypeEnum, string>,
  type?: NotificationTypeEnum,
) {
  switch (keyValue) {
    case NotificationResourceTypeEnum.MATCH_EVENT_ID:
    case NotificationResourceTypeEnum.EVENT_PARTICIPANT_ID:
    case NotificationResourceTypeEnum.TEAM_ID:
      if (
        !resources[NotificationResourceTypeEnum.EVENT_ID] &&
        !resources[NotificationResourceTypeEnum.MATCH_EVENT_ID] &&
        !resources[NotificationResourceTypeEnum.TEAM_ID]
      ) {
        return '/';
      }
      if (type === NotificationTypeEnum.PARTICIPATION_EVENT) {
        return routesConfig.parametersDetailsEvent.participants.to(
          resources[NotificationResourceTypeEnum.EVENT_ID],
          value,
        );
      }
      if (type === NotificationTypeEnum.INVITATION_TEAM_PARTICIPATION_EVENT) {
        return routesConfig.eventViewDetails.eventViewPlayersTeamOpen.to(
          resources[NotificationResourceTypeEnum.EVENT_ID],
          resources[NotificationResourceTypeEnum.EVENT_PARTICIPANT_ID],
        );
      }
      if (type === NotificationTypeEnum.SCORE_UPDATED) {
        console.log('res', resources[NotificationResourceTypeEnum.MATCH_EVENT_ID]);

        return routesConfig.eventViewDetails.eventViewMatchs.to(
          resources[NotificationResourceTypeEnum.MATCH_EVENT_ID],
        );
      }
      return routesConfig.eventViewDetails.eventViewInfos.to(
        resources[NotificationResourceTypeEnum.EVENT_ID],
      );
    case NotificationResourceTypeEnum.CLASSEMENT_EVENT_ID:
      return routesConfig.eventViewDetails.eventViewScoreboard.to(value);
    case NotificationResourceTypeEnum.STRUCTURE_ID:
      return routesConfig.structureProfilDetails.to(value);
    case NotificationResourceTypeEnum.EVENT_ID:
      if (type === NotificationTypeEnum.EVENT_ANNONCE) {
        console.log('res', resources[NotificationResourceTypeEnum.EVENT_ID]);

        return routesConfig.eventViewDetails.eventViewAnnonces.to(
          resources[NotificationResourceTypeEnum.EVENT_ID],
        );
      }
      return routesConfig.eventViewDetails.eventViewInfos.to(value);
    case NotificationResourceTypeEnum.CONVERSATION_ID:
      return routesConfig.messages.to();
    case NotificationResourceTypeEnum.RELATION_ID:
      return routesConfig.relationPage.to();
    default:
      return '/';
  }
}

export const parseRessourceFromNotificaton = (notification: UnpyNotification) => {
  const keys = notification?.resources ? Object.keys(notification.resources) : [];
  const ressourcesParsed: {
    keyValue: string;
    // @ts-ignore
    value: string;
    isLink: boolean;
  }[] = keys.reduce((acc, key) => {
    // @ts-ignore
    acc.push({
      keyValue: key,
      // @ts-ignore
      value: notification.resources[key],
      isLink: key.includes('ID'),
    });
    return acc;
  }, []);

  return ressourcesParsed;
};

export const NotificationListItem = ({
  notification,
  className,
  closePreview,
  reFetchNotification,
}: notificationListItemProps) => {
  const t = useTranslate();
  const { notificationRepository } = useContextDependency();
  const dispatch = useDispatch();
  const [alreadyViewed, setAlreadyViewed] = useState<boolean>(notification.viewed);
  const setViewedNotification = () => {
    if (alreadyViewed || !notification.id) return;
    setAlreadyViewed(true);
    notificationRepository.toViewed(notification.id).then(() => {
      dispatch(decrementNumberUnviewed());
    });
    return;
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (!notification.viewed) {
      setViewedNotification();
    }
  }, []);

  return (
    <Card className={classNames('notificationItem', className)}>
      <div className={'notificationItem__header'}>
        <KeyValue
          className={'-margedRight'}
          accent
          keyValue={''}
          value={<TagPlayer embed player={notification.userFrom} />}
        />
        <KeyValue
          accent
          keyValue={''}
          value={formatDate(notification.creationDate, 'readable(with hour)')}
        />
        {!alreadyViewed && <div className={'view-indicator'} />}
      </div>
      <KeyValue
        accent
        className={'-topMarged'}
        keyValue={''}
        value={t(
          `notificationList.item.objectByType.${notification.type}` as TranslationType,
          {
            user: notification.userFrom.idPlayer,
          },
        )}
      />

      <div className={'notificationItem__content -topMarged'}>
        {ACTION_COMP_BY_NOTIFICATION_TYPE[notification.type]?.(
          notification,
          reFetchNotification,
          closePreview,
        )}
      </div>
    </Card>
  );
};
